/**
 * High-Variant Product Support - Section Rendering API
 *
 * For products with 250+ variants, uses Shopify's Section Rendering API
 * to dynamically update option availability and variant data when the user
 * selects an option value.
 *
 * Flow:
 * 1. User clicks a swatch option
 * 2. We collect all selected option_value IDs from data attributes
 * 3. Fetch the section HTML with ?option_values=id1,id2,... param
 * 4. Parse the response and update:
 *    - Swatch availability (show/hide based on tw-hidden class from product_option_value.available)
 *    - Shopify.optionsMap (rebuilt from visible swatches so updateOptionsInSelector works)
 *    - Hidden variant <select> value
 *    - ProductJson data
 *    - Inventory spans
 */
(function() {
  'use strict';

  if (!window.highVariantProduct) return;

  var sectionId = window.highVariantSectionId;
  var productUrl = window.highVariantProductUrl;
  var abortController = null;
  var debounceTimer = null;
  // Flag to prevent our own Section Rendering fetch from re-triggering
  var isSectionRendering = false;
  var isFetchingSection = false;
  var pendingSelectionValues = null;
  var activeChangeContext = null;
  var latestRequestId = 0;
  var queuedSwatchInput = null;
  var sectionHtmlCache = Object.create(null);
  var sectionHtmlPromiseCache = Object.create(null);
  var sectionHtmlCacheKeys = [];
  var maxSectionHtmlCacheEntries = 30;
  var prefetchTimer = null;
  var pageLoadWarmTimer = null;
  var pageLoadWarmStarted = false;
  var maxPageLoadWarmTargets = 8;

  /**
   * Monkey-patch slate.Variants._getVariantFromOptions so that when the
   * normal lookup fails (variant beyond the 250 cap), we fall back to
   * the variant data extracted from the Section Rendering API response.
   */
  var _patchApplied = false;
  function patchVariantLookup() {
    if (_patchApplied) return;
    if (typeof slate === 'undefined' || !slate.Variants) return;
    _patchApplied = true;

    var original = slate.Variants.prototype._getVariantFromOptions;
    slate.Variants.prototype._getVariantFromOptions = function() {
      var found;
      try {
        found = original.call(this);
      } catch (e) {
        // _getCurrentOptions in theme.js can crash on undefined.split()
        // when swatch states are in flux. Swallow and fall through to our lookup.
        found = undefined;
      }
      if (!found && window.highVariantProduct && window._highVariantSelectedVariant) {
        var candidate = window._highVariantSelectedVariant;
        // Verify candidate matches currently selected option values
        // Read directly from .single-option-selector selects to avoid
        // _getCurrentOptions side effects that can crash
        var selects = document.querySelectorAll('.single-option-selector-' + sectionId);
        var matches = true;
        selects.forEach(function(sel, i) {
          var key = 'option' + (i + 1);
          if (candidate[key] && sel.value && candidate[key] !== sel.value) {
            matches = false;
          }
        });
        if (matches) {
          found = candidate;
          // Inject into product.variants so future lookups can find it natively
          var alreadyExists = false;
          for (var i = 0; i < this.product.variants.length; i++) {
            if (this.product.variants[i].id === found.id) { alreadyExists = true; break; }
          }
          if (!alreadyExists) {
            this.product.variants.push(found);
          }
        }
      }
      return found;
    };
  }

  // Patch as early as possible; retry on DOMContentLoaded to cover timing edge cases
  patchVariantLookup();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', patchVariantLookup);
  } else {
    setTimeout(patchVariantLookup, 0);
  }

  // Patch slate.Variants _onSelectChange to be a no-op for high-variant products
  // This prevents theme.js from trying to access Shopify.productMeta which doesn't have
  // entries for variants beyond 250. Our Section Rendering API handles all updates.
  if (typeof slate !== 'undefined' && slate.Variants) {
    var originalOnSelectChange = slate.Variants.prototype._onSelectChange;
    
    slate.Variants.prototype._onSelectChange = function() {
      if (window.highVariantProduct) return; // no-op for high-variant products
      return originalOnSelectChange.call(this);
    };
  }

  // Patch handleSelectedSwatch to skip thumbnail click for high-variant products
  // The thumbnail click causes a duplicate swatch change event. Our Section Rendering
  // API handles the image update, so we don't need theme.js to do it.
  if (typeof window !== 'undefined' && window.handleSelectedSwatch) {
    var originalHandleSelectedSwatch = window.handleSelectedSwatch;
    window.handleSelectedSwatch = function(item) {
      if (window.highVariantProduct) {
        // For high-variant products, only trigger the input click, not the thumbnail
        const inputId = $(item).data('id');
        const input = $(`#${inputId}`);
        if(input.length) input.trigger('click');
        // Skip the thumbnail click and other theme.js logic
        return;
      }
      return originalHandleSelectedSwatch.call(this, item);
    };
  }

  // Patch handleSelectedImg to skip clicking already-checked swatches for high-variant products
  // This prevents the swatch from being toggled off when the thumbnail is clicked
  if (typeof window !== 'undefined' && window.handleSelectedImg) {
    var originalHandleSelectedImg = window.handleSelectedImg;
    window.handleSelectedImg = function(element) {
      if (window.highVariantProduct) {
        // For high-variant products, only click the swatch if it's not already checked
        var swatchId = $(element).data('image-id');
        if (swatchId && swatchId.length) {
          var inputColorSwatch = $("input[data-image-id='" + swatchId + "']");
          inputColorSwatch = inputColorSwatch.filter('[data-type="color"]');
          if (!inputColorSwatch.length) {
            return;
          }
          if (inputColorSwatch && inputColorSwatch.length === 1) {
            // Only one swatch with this image ID
            if (!inputColorSwatch.prop('checked')) {
              inputColorSwatch.click();
            }
          } else if (inputColorSwatch && inputColorSwatch.length > 1) {
            // Multiple swatches with this image ID - click the first unchecked one
            var clicked = false;
            inputColorSwatch.each(function() {
              if (!clicked && !$(this).prop('checked')) {
                $(this).click();
                clicked = true;
              }
            });
          }
        }
        return;
      }
      return originalHandleSelectedImg.call(this, element);
    };
  }

  /**
   * Collect selected option value IDs from the swatch inputs.
   * Each swatch input rendered via product_option_value API has
   * a parent .swatch-element with data-option-value-id attribute.
   */
  function getSelectedOptionValueIds() {
    var ids = [];
    document.querySelectorAll('.swatch .swatchInput:checked').forEach(function(input) {
      var swatchEl = input.closest('.swatch-element');
      if (swatchEl && swatchEl.dataset.optionValueId) {
        ids.push(swatchEl.dataset.optionValueId);
      }
    });
    return ids;
  }

  /**
   * Build the Section Rendering API URL with option_values parameter.
   * Format: /products/handle?section_id=xxx&option_values=id1,id2,...
   */
  function buildSectionUrl(optionValueIds) {
    var params = ['section_id=' + encodeURIComponent(sectionId)];
    if (optionValueIds.length > 0) {
      params.push('option_values=' + optionValueIds.join(','));
    }
    return productUrl + '?' + params.join('&');
  }

  /**
   * Fetch the section HTML and update the page.
   */
  function fetchAndUpdateSection(optionValueIds, changedOptionIndex, isResolvedSelectionFetch) {
    var requestId = ++latestRequestId;
    setHighVariantLoading(true);

    if (abortController) {
      abortController.abort();
    }
    abortController = new AbortController();

    var url = buildSectionUrl(optionValueIds);
    var htmlPromise = getSectionHtml(url, abortController.signal);

    htmlPromise.then(function(html) {
        if (requestId !== latestRequestId) {
          return;
        }

        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');

        isSectionRendering = true;
        extractSelectedVariant(doc);

        var nextOptionIndexToResolve = getNextOptionIndexToResolve(changedOptionIndex);
        var shouldRunResolvedSelectionFetch =
          !isResolvedSelectionFetch &&
          typeof nextOptionIndexToResolve === 'number';

        if (shouldRunResolvedSelectionFetch) {
          applyPendingSelectionDefaultAtIndex(nextOptionIndexToResolve, doc, true);
          fetchAndUpdateSection(
            getOptionValueIdsForSelectionValues(pendingSelectionValues, nextOptionIndexToResolve),
            nextOptionIndexToResolve,
            nextOptionIndexToResolve >= getLastOptionIndex()
          );
          return;
        }

        updateSwatchAvailability(doc, {
          preserveColorVisibility: typeof changedOptionIndex === 'number' && changedOptionIndex < getLastOptionIndex()
        });
        rebuildOptionsMap(doc);

        updateVariantSelect(doc);

        var currentSelect = document.querySelector('#ProductSelect-' + sectionId);
        var currentVariantId = currentSelect && currentSelect.value
          ? currentSelect.value
          : (window._highVariantSelectedVariant && window._highVariantSelectedVariant.id);

        var pdpContainer = document.querySelector('.pdp-container');
        if (pdpContainer && currentVariantId) {
          pdpContainer.setAttribute('data-current-variant', currentVariantId);
        }

        hydrateVariantQuantityMetaFromResponse(doc, currentVariantId);
        updateProductJson(doc);
        updateQuantityConstraints(doc);

        // Prioritize the buying-critical UI first so the PDP feels responsive.
        // Heavier secondary content (gallery/specs/product info) is deferred
        // until after the browser has a chance to paint these changes.
        updatePriceDisplay(doc);
        updateAddToCartArea(doc);
        syncCurrentVariantUnitPrice();
        restoreSwatchCheckedState();

        if (typeof updateProductQuantity === 'function') {
          updateProductQuantity();
        }
        if (typeof UpdatePriceOnCarts === 'function') {
          var quantityInput = document.querySelector('.product-single .product-form__input[name="quantity"]');
          var currentQty = quantityInput ? parseInt(quantityInput.value, 10) : 1;
          UpdatePriceOnCarts(currentQty && currentQty > 0 ? currentQty : 1);
        }

        scheduleSecondaryVariantUpdates(doc, requestId);

        if (requestId === latestRequestId) {
          activeChangeContext = null;
          setHighVariantLoading(false);
        }

        isSectionRendering = false;
        flushQueuedSwatchClick();
      })
      .catch(function(error) {
        if (requestId === latestRequestId) {
          isSectionRendering = false;
          activeChangeContext = null;
          setHighVariantLoading(false);
          flushQueuedSwatchClick();
        }
        if (error.name !== 'AbortError') {
          console.error('[HighVariant] Section rendering error:', error);
        }
      });
  }

  function getVariantCountFromHtml(html) {
    if (!html) return null;

    var parser = new DOMParser();
    var doc = parser.parseFromString(html, 'text/html');
    var responseSelect = doc.querySelector('#ProductSelect-' + sectionId);
    return responseSelect ? responseSelect.options.length : null;
  }

  function getVariantSignatureFromSelect(select) {
    if (!select) return null;

    return Array.prototype.map.call(select.options, function(option) {
      return [
        option.value || '',
        option.textContent ? option.textContent.trim() : ''
      ].join('::');
    }).join('|');
  }

  function getVariantSignatureFromHtml(html) {
    if (!html) return null;

    var parser = new DOMParser();
    var doc = parser.parseFromString(html, 'text/html');
    var responseSelect = doc.querySelector('#ProductSelect-' + sectionId);
    return getVariantSignatureFromSelect(responseSelect);
  }

  function getCurrentVariantCount() {
    var currentSelect = document.querySelector('#ProductSelect-' + sectionId);
    return currentSelect ? currentSelect.options.length : null;
  }

  function getCurrentVariantSignature() {
    var currentSelect = document.querySelector('#ProductSelect-' + sectionId);
    return getVariantSignatureFromSelect(currentSelect);
  }

  function cacheSectionHtml(url, html) {
    if (!url || !html) return;

    if (!sectionHtmlCache[url]) {
      sectionHtmlCacheKeys.push(url);
    }
    sectionHtmlCache[url] = {
      html: html,
      variantCount: getVariantCountFromHtml(html),
      variantSignature: getVariantSignatureFromHtml(html)
    };

    while (sectionHtmlCacheKeys.length > maxSectionHtmlCacheEntries) {
      var oldestUrl = sectionHtmlCacheKeys.shift();
      delete sectionHtmlCache[oldestUrl];
    }
  }

  function getCachedSectionHtml(url) {
    var cachedEntry = sectionHtmlCache[url];
    if (!cachedEntry) return null;

    var currentVariantCount = getCurrentVariantCount();
    var currentVariantSignature = getCurrentVariantSignature();
    if (
      cachedEntry.variantCount !== null &&
      currentVariantCount !== null &&
      currentVariantCount > cachedEntry.variantCount
    ) {
      delete sectionHtmlCache[url];
      return null;
    }

    if (
      cachedEntry.variantSignature !== null &&
      currentVariantSignature !== null &&
      cachedEntry.variantSignature !== currentVariantSignature
    ) {
      delete sectionHtmlCache[url];
      return null;
    }

    return cachedEntry.html || null;
  }

  function getSectionHtml(url, signal) {
    var cachedHtml = getCachedSectionHtml(url);
    if (cachedHtml) {
      return Promise.resolve(cachedHtml);
    }

    if (sectionHtmlPromiseCache[url]) {
      return sectionHtmlPromiseCache[url];
    }

    var fetchOptions = signal ? { signal: signal } : {};
    var promise = fetch(url, fetchOptions)
      .then(function(response) {
        return response.text();
      })
      .then(function(html) {
        cacheSectionHtml(url, html);
        delete sectionHtmlPromiseCache[url];
        return html;
      })
      .catch(function(error) {
        delete sectionHtmlPromiseCache[url];
        throw error;
      });

    sectionHtmlPromiseCache[url] = promise;
    return promise;
  }

  function scheduleSecondaryVariantUpdates(doc, requestId) {
    runAfterNextPaint(function() {
      if (requestId !== latestRequestId) return;

      isSectionRendering = true;
      try {
        updateInventorySpans(doc);
        updateSKUAndStockLabel(doc);
        updateProductInfoAndSpecs(doc);
        updateGallery(doc);
        restoreSwatchCheckedState();
      } finally {
        isSectionRendering = false;
      }
    });
  }

  function runAfterNextPaint(callback) {
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(function() {
        window.requestAnimationFrame(callback);
      });
      return;
    }

    setTimeout(callback, 0);
  }

  function setHighVariantLoading(isLoading) {
    isFetchingSection = isLoading;
    document.documentElement.classList.toggle('high-variant-loading', isLoading);

    var productContainer = document.querySelector('.tw-kanopi-product') || document.querySelector('.pdp-container');
    if (productContainer) {
      productContainer.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }
  }

  function getSwatchInputFromTarget(target) {
    if (!target || !target.closest) return null;

    var input = target.closest('.swatchInput');
    if (input) return input;

    var swatchElement = target.closest('.swatch-element');
    if (swatchElement) {
      return swatchElement.querySelector('.swatchInput');
    }

    var label = target.closest('label');
    if (label && label.getAttribute('for')) {
      return document.getElementById(label.getAttribute('for'));
    }

    return null;
  }

  function flushQueuedSwatchClick() {
    if (!queuedSwatchInput || isFetchingSection) return;

    var input = queuedSwatchInput;
    queuedSwatchInput = null;

    if (!isSwatchInputSelectable(input) || input.checked) return;

    setTimeout(function() {
      input.click();
    }, 0);
  }

  function isSwatchInputSelectable(input) {
    if (!input || !document.body.contains(input) || input.disabled) return false;

    var swatchElement = input.closest('.swatch-element');
    if (swatchElement && swatchElement.classList.contains('tw-hidden')) return false;

    return true;
  }

  function schedulePrefetchForInput(input) {
    if (!isSwatchInputSelectable(input) || input.checked) return;

    clearTimeout(prefetchTimer);
    prefetchTimer = setTimeout(function() {
      prefetchSelectionForInput(input);
    }, 80);
  }

  function prefetchSelectionForInput(input) {
    if (!isSwatchInputSelectable(input)) return;

    var swatchGroup = input.closest('.swatch[data-option-index]');
    var changedOptionIndex = swatchGroup ? parseInt(swatchGroup.dataset.optionIndex, 10) : null;
    if (typeof changedOptionIndex !== 'number') return;

    var selectionValues = getCheckedSwatchValuesByIndex();
    selectionValues[changedOptionIndex] = input.value;

    var optionValueIds = getOptionValueIdsForSelectionValues(selectionValues, changedOptionIndex);
    if (!optionValueIds.length) return;

    prefetchSectionForSelection(optionValueIds, changedOptionIndex, selectionValues, false);
  }

  function prefetchSectionForSelection(optionValueIds, changedOptionIndex, selectionValues, isResolvedSelectionFetch) {
    var url = buildSectionUrl(optionValueIds);
    if (getCachedSectionHtml(url)) return;

    getSectionHtml(url)
      .then(function(html) {
        var shouldPrefetchResolvedSelection =
          !isResolvedSelectionFetch &&
          typeof changedOptionIndex === 'number' &&
          changedOptionIndex < document.querySelectorAll('.swatch[data-option-index]').length - 1;

        if (!shouldPrefetchResolvedSelection) return;

        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var resolvedSelectionValues = selectionValues.slice();

        applySelectionDefaults(resolvedSelectionValues, changedOptionIndex, doc);

        var resolvedOptionValueIds = getOptionValueIdsForSelectionValues(resolvedSelectionValues);
        if (resolvedOptionValueIds.length) {
          prefetchSectionForSelection(resolvedOptionValueIds, changedOptionIndex, resolvedSelectionValues, true);
        }
      })
      .catch(function() {
        // Prefetching is opportunistic; the click path will fetch normally if needed.
      });
  }

  function schedulePageLoadWarmup() {
    if (pageLoadWarmStarted) return;
    pageLoadWarmStarted = true;

    var warmup = function() {
      pageLoadWarmTimer = setTimeout(function() {
        warmLikelySelectionsOnLoad();
      }, 300);
    };

    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(warmup, { timeout: 1200 });
      return;
    }

    warmup();
  }

  function warmLikelySelectionsOnLoad() {
    var warmTargets = [];
    var currentSelections = getCheckedSwatchValuesByIndex();
    var groups = Array.prototype.slice.call(
      document.querySelectorAll('.swatch[data-option-index]')
    );

    groups.forEach(function(group) {
      if (warmTargets.length >= maxPageLoadWarmTargets) return;

      var optionIndex = parseInt(group.dataset.optionIndex, 10);
      if (isNaN(optionIndex) || optionIndex > 1) return;

      var inputs = Array.prototype.filter.call(
        group.querySelectorAll('.swatchInput'),
        function(input) {
          return isSwatchInputSelectable(input);
        }
      );

      inputs.forEach(function(input) {
        if (warmTargets.length >= maxPageLoadWarmTargets) return;
        if (input.checked) return;

        var selectionValues = currentSelections.slice();
        selectionValues[optionIndex] = input.value;

        var optionValueIds = getOptionValueIdsForSelectionValues(selectionValues, optionIndex);
        if (!optionValueIds.length) return;

        warmTargets.push({
          optionValueIds: optionValueIds,
          changedOptionIndex: optionIndex,
          selectionValues: selectionValues
        });
      });
    });

    runWarmTargetsSequentially(warmTargets, 0);
  }

  function runWarmTargetsSequentially(targets, index) {
    if (!targets || index >= targets.length) return;

    var target = targets[index];

    prefetchSectionForSelection(
      target.optionValueIds,
      target.changedOptionIndex,
      target.selectionValues,
      false
    );

    setTimeout(function() {
      runWarmTargetsSequentially(targets, index + 1);
    }, 120);
  }

  function getCheckedSwatchValuesByIndex() {
    return Array.prototype.map.call(
      document.querySelectorAll('.swatch[data-option-index]'),
      function(group) {
        var checkedInput = group.querySelector('.swatchInput:checked');
        return checkedInput ? checkedInput.value : null;
      }
    );
  }

  function getOptionValueIdsForSelectionValues(selectionValues, maxOptionIndex) {
    if (!selectionValues || !selectionValues.length) return [];

    return Array.prototype.map.call(
      document.querySelectorAll('.swatch[data-option-index]'),
      function(group) {
        var optionIndex = parseInt(group.dataset.optionIndex, 10);
        if (typeof maxOptionIndex === 'number' && optionIndex > maxOptionIndex) {
          return null;
        }
        var targetValue = selectionValues[optionIndex];
        if (!targetValue) return null;

        var matchingElement = Array.prototype.find.call(
          group.querySelectorAll('.swatch-element[data-option-value-id]'),
          function(el) {
            return el.dataset.value === targetValue;
          }
        );

        return matchingElement ? matchingElement.dataset.optionValueId : null;
      }
    ).filter(Boolean);
  }

  function applyPendingSelectionDefaults(changedOptionIndex, sourceDoc) {
    if (typeof changedOptionIndex !== 'number' || !pendingSelectionValues) return false;

    return applySelectionDefaults(pendingSelectionValues, changedOptionIndex, sourceDoc);
  }

  function getLastOptionIndex() {
    return Array.prototype.reduce.call(
      document.querySelectorAll('.swatch[data-option-index]'),
      function(maxIndex, group) {
        var optionIndex = parseInt(group.dataset.optionIndex, 10);
        return isNaN(optionIndex) ? maxIndex : Math.max(maxIndex, optionIndex);
      },
      -1
    );
  }

  function getNextOptionIndexToResolve(changedOptionIndex) {
    if (typeof changedOptionIndex !== 'number') return null;

    var nextOptionIndex = changedOptionIndex + 1;
    return nextOptionIndex <= getLastOptionIndex() ? nextOptionIndex : null;
  }

  function applyPendingSelectionDefaultAtIndex(optionIndex, sourceDoc, forceFirst) {
    if (typeof optionIndex !== 'number' || !pendingSelectionValues) return false;

    var source = sourceDoc || document;
    var group = source.querySelector('.swatch[data-option-index="' + optionIndex + '"]');
    if (!group) return false;

    var visibleValues = Array.prototype.map.call(
      group.querySelectorAll('.swatch-element[data-option-value-id]'),
      function(el) {
        return isSwatchElementAvailable(el) ? el.dataset.value : null;
      }
    ).filter(Boolean);

    if (!visibleValues.length) return false;

    var currentPendingValue = pendingSelectionValues[optionIndex];
    if (!forceFirst && currentPendingValue && visibleValues.indexOf(currentPendingValue) !== -1) {
      return false;
    }

    if (currentPendingValue === visibleValues[0]) return false;

    pendingSelectionValues[optionIndex] = visibleValues[0];
    return true;
  }

  function applySelectionDefaults(selectionValues, changedOptionIndex, sourceDoc) {
    if (typeof changedOptionIndex !== 'number' || !selectionValues) return false;

    var changed = false;
    var swatchGroups = Array.prototype.slice.call(
      (sourceDoc || document).querySelectorAll('.swatch[data-option-index]')
    );
    var lastOptionIndex = swatchGroups.reduce(function(maxIndex, group) {
      var optionIndex = parseInt(group.dataset.optionIndex, 10);
      return isNaN(optionIndex) ? maxIndex : Math.max(maxIndex, optionIndex);
    }, -1);

    swatchGroups.forEach(function(group) {
      var optionIndex = parseInt(group.dataset.optionIndex, 10);
      if (optionIndex <= changedOptionIndex) return;

      var visibleValues = Array.prototype.map.call(
        group.querySelectorAll('.swatch-element[data-option-value-id]'),
        function(el) {
          return isSwatchElementAvailable(el) ? el.dataset.value : null;
        }
      ).filter(Boolean);

      if (!visibleValues.length) return;

      var currentPendingValue = selectionValues[optionIndex];
      var shouldDefaultToFirstColor =
        optionIndex === lastOptionIndex &&
        isColorSwatchGroup(group);
      var shouldDefaultFirstDownstreamOption =
        changedOptionIndex === 0 &&
        optionIndex > changedOptionIndex;

      if (shouldDefaultFirstDownstreamOption || shouldDefaultToFirstColor) {
        if (currentPendingValue !== visibleValues[0]) {
          selectionValues[optionIndex] = visibleValues[0];
          changed = true;
        }
        return;
      }

      if (currentPendingValue && visibleValues.indexOf(currentPendingValue) !== -1) {
        return;
      }

      selectionValues[optionIndex] = visibleValues[0];
      changed = true;
    });

    return changed;
  }

  function isColorSwatchGroup(group) {
    if (!group) return false;
    if (group.classList.contains('swatch-color')) return true;
    if (group.querySelector('.swatch-element.is-color')) return true;

    var input = group.querySelector('.swatchInput[data-type]');
    return !!input && input.dataset.type === 'color';
  }

  function applyPendingSelectionToCurrentDom() {
    if (!pendingSelectionValues) return;

    document.querySelectorAll('.swatch[data-option-index]').forEach(function(group) {
      var optionIndex = parseInt(group.dataset.optionIndex, 10);
      var targetValue = pendingSelectionValues[optionIndex];
      if (!targetValue) return;

      var targetInput = group.querySelector('.swatchInput[value="' + targetValue + '"]');
      if (targetInput) {
        group.querySelectorAll('.swatchInput').forEach(function(input) {
          input.checked = false;
        });
        targetInput.checked = true;
      }

      var selector = document.querySelector(
        '.single-option-selector-' + sectionId + '[data-index="option' + (optionIndex + 1) + '"]'
      );
      if (selector) {
        selector.value = targetValue;
      }

      var label = group.querySelector('.slVariant');
      if (label) {
        label.textContent = targetValue;
      }
    });
  }

  function isSwatchElementAvailable(el) {
    return !!el &&
      el.style.display !== 'none' &&
      !el.classList.contains('tw-hidden');
  }

  function getVisibleSwatchOptionValueIds(group) {
    if (!group) return [];

    return Array.prototype.map.call(
      group.querySelectorAll('.swatch-element[data-option-value-id]'),
      function(el) {
        return isSwatchElementAvailable(el) ? el.dataset.optionValueId : null;
      }
    ).filter(Boolean);
  }

  function getCheckedSwatchValue(group) {
    if (!group) return null;

    var checkedInput = group.querySelector('.swatchInput:checked');
    return checkedInput ? checkedInput.value : null;
  }

  function isColorGroupStateUnchanged(currentGroup, newGroup) {
    var currentVisibleIds = getVisibleSwatchOptionValueIds(currentGroup);
    var newVisibleIds = getVisibleSwatchOptionValueIds(newGroup);

    if (currentVisibleIds.length !== newVisibleIds.length) {
      return false;
    }

    for (var i = 0; i < currentVisibleIds.length; i++) {
      if (currentVisibleIds[i] !== newVisibleIds[i]) {
        return false;
      }
    }

    return getCheckedSwatchValue(currentGroup) === getCheckedSwatchValue(newGroup);
  }

  /**
   * Restore the checked state of swatches based on the selected variant.
   * The Section Rendering response doesn't know which variant was selected,
   * so we need to re-check the swatches that match the currently selected variant.
   */
  function restoreSwatchCheckedState() {
    var select = document.querySelector('#ProductSelect-' + sectionId);
    if (!select || !select.value) return;

    var selectedOption = select.querySelector('option[value="' + select.value + '"]');
    if (!selectedOption) return;

    // Get the selected variant's title to match against swatch values
    var selectedTitle = selectedOption.textContent;

    // Parse the selected variant title to get individual option values
    // Title format: "Option1 / Option2 / Option3"
    var optionValues = selectedTitle.split(' / ').map(function(v) { return v.trim(); });

    // Update swatches by option index so duplicate values in other groups cannot
    // restore the wrong selection. Only touch groups whose checked value
    // actually changed to avoid visible flicker on stable swatch sets.
    optionValues.forEach(function(optionValue, optionIndex) {
      var group = document.querySelector('.swatch[data-option-index="' + optionIndex + '"]');
      if (!group) return;

      var currentChecked = group.querySelector('.swatchInput:checked');
      if (currentChecked && currentChecked.value === optionValue) {
        return;
      }

      if (currentChecked) {
        currentChecked.checked = false;
      }

      var matchingSwatch = group.querySelector('.swatchInput[value="' + optionValue + '"]');
      if (matchingSwatch && !matchingSwatch.checked) {
        matchingSwatch.checked = true;
      }
    });

    optionValues.forEach(function(optionValue, optionIndex) {
      var selector = document.querySelector(
        '.single-option-selector-' + sectionId + '[data-index="option' + (optionIndex + 1) + '"]'
      );
      if (selector) {
        selector.value = optionValue;
      }

      var label = document.querySelector('.swatch[data-option-index="' + optionIndex + '"] .slVariant');
      if (label) {
        label.textContent = optionValue;
      }
    });
  }

  function hydrateInitialHighVariantState() {
    var select = document.querySelector('#ProductSelect-' + sectionId);
    if (!select || !select.value) return;

    var selectedOption = select.querySelector('option[value="' + select.value + '"]');
    if (selectedOption) {
      syncSelectedVariantFromOption(selectedOption);
    }

    var pdpContainer = document.querySelector('.pdp-container');
    if (pdpContainer) {
      pdpContainer.setAttribute('data-current-variant', select.value);
    }

    restoreSwatchCheckedState();
  }

  /**
   * Update swatch element visibility based on the new section HTML.
   * The re-rendered HTML has tw-hidden on unavailable option values
   * (from product_option_value.available check in swatch.liquid).
   */
  function updateSwatchAvailability(doc, options) {
    options = options || {};
    var newSwatchGroups = doc.querySelectorAll('.swatch[data-option-index]');

    newSwatchGroups.forEach(function(newGroup) {
      var optionIndex = newGroup.dataset.optionIndex;
      var currentGroup = document.querySelector('.swatch[data-option-index="' + optionIndex + '"]');
      if (!currentGroup) return;
      var isColorGroup =
        isColorSwatchGroup(currentGroup) &&
        isColorSwatchGroup(newGroup);
      if (options.colorOnly && !isColorGroup) return;
      if (options.preserveColorVisibility && isColorGroup) return;
      if (isColorGroup && isColorGroupStateUnchanged(currentGroup, newGroup)) {
        return;
      }

      // Build a map of new swatch elements by option value ID
      var newElements = newGroup.querySelectorAll('.swatch-element[data-option-value-id]');
      var newElementMap = {};
      newElements.forEach(function(el) {
        newElementMap[el.dataset.optionValueId] = el;
      });

      // Update each current swatch element
      var currentElements = currentGroup.querySelectorAll('.swatch-element[data-option-value-id]');
      currentElements.forEach(function(currentEl) {
        var ovId = currentEl.dataset.optionValueId;
        var newEl = newElementMap[ovId];

        if (!newEl) return;

        if (isColorGroup) {
          var shouldRemainVisible = !!newEl && isSwatchElementAvailable(newEl);
          if (shouldRemainVisible) {
            if (currentEl.style.display === 'none') {
              currentEl.style.display = '';
            }
          } else if (currentEl.style.display !== 'none') {
            currentEl.style.display = 'none';
          }
        } else {
          // Match the normal PDP selector flow: hide unavailable option values
          // with inline display and let downstream defaulting pick the first
          // visible value when the current one is no longer valid.
          currentEl.style.display = newEl.style.display === 'none' ? 'none' : '';
        }
        currentEl.classList.remove('high-variant-option-unavailable');
        currentEl.removeAttribute('aria-disabled');

        // Update the variant ID and checked state on the input
        var currentInput = currentEl.querySelector('.swatchInput');
        var newInput = newEl.querySelector('.swatchInput');
        if (currentInput && newInput) {
          currentInput.disabled = false;
          if (newInput.dataset.varId) {
            currentInput.dataset.varId = newInput.dataset.varId;
          }
          if (newInput.dataset.symbol) {
            currentInput.dataset.symbol = newInput.dataset.symbol;
          }
        }

        // Update image data attributes
        var currentSwatchItem = currentEl.querySelector('.js-swatch-item');
        var newSwatchItem = newEl.querySelector('.js-swatch-item');
        if (currentSwatchItem && newSwatchItem) {
          if (newSwatchItem.dataset.imageId) {
            currentSwatchItem.dataset.imageId = newSwatchItem.dataset.imageId;
          }
        }
      });
    });
  }

  /**
   * Rebuild Shopify.optionsMap from the new section HTML so that
   * updateOptionsInSelector works correctly for subsequent changes.
   * We parse the visible (non-tw-hidden) swatch elements to build the map.
   */
  function rebuildOptionsMap(doc) {
    if (typeof Shopify === 'undefined') return;

    Shopify.optionsMap = {};
    var newSwatchGroups = doc.querySelectorAll('.swatch[data-option-index]');

    newSwatchGroups.forEach(function(group) {
      var optionIndex = parseInt(group.dataset.optionIndex, 10);
      var allElements = group.querySelectorAll('.swatch-element[data-option-value-id]');
      var visibleElements = [];
      allElements.forEach(function(el) {
        if (el.style.display !== 'none') visibleElements.push(el);
      });
      var values = [];
      visibleElements.forEach(function(el) {
        var val = el.dataset.value;
        if (val && values.indexOf(val) === -1) {
          values.push(val);
        }
      });

      if (optionIndex === 0) {
        // Root key for option1
        Shopify.optionsMap['root'] = values;
      } else if (optionIndex === 1) {
        // Key is the selected option1 value
        var opt1Input = document.querySelector('.swatch[data-option-index="0"] .swatchInput:checked');
        if (!opt1Input) {
          // Fallback: check the new doc
          opt1Input = doc.querySelector('.swatch[data-option-index="0"] .swatchInput:checked');
        }
        if (opt1Input) {
          var key = opt1Input.value;
          Shopify.optionsMap[key] = values;
        }
      } else if (optionIndex === 2) {
        // Key is "option1 / option2"
        var opt1Input2 = document.querySelector('.swatch[data-option-index="0"] .swatchInput:checked');
        var opt2Input = document.querySelector('.swatch[data-option-index="1"] .swatchInput:checked');
        if (!opt1Input2) opt1Input2 = doc.querySelector('.swatch[data-option-index="0"] .swatchInput:checked');
        if (!opt2Input) opt2Input = doc.querySelector('.swatch[data-option-index="1"] .swatchInput:checked');
        if (opt1Input2 && opt2Input) {
          var key2 = opt1Input2.value + ' / ' + opt2Input.value;
          Shopify.optionsMap[key2] = values;
        }
      }
    });
  }

  /**
   * Copy the price container HTML from the response to the current page.
   */
  function updatePriceDisplay(doc) {
    // Main price area (per-sqft + per-carton)
    var newPrice = doc.querySelector('.js-price-container');
    var currentPrice = document.querySelector('.js-price-container');
    if (newPrice && currentPrice) {
      currentPrice.innerHTML = newPrice.innerHTML;
    }


    if (newPrice) {
      document.querySelectorAll('.js-price-container').forEach(function(el) {
        el.innerHTML = newPrice.innerHTML;
      });
    }

    // Regular price / discount wrapper
    var newReg = doc.querySelector('.js-reg-price-wrapper');
    var currentReg = document.querySelector('.js-reg-price-wrapper');
    if (newReg && currentReg) {
      currentReg.innerHTML = newReg.innerHTML;
      currentReg.className = newReg.className;
    }

    // Remove loading state from price element
    var priceEl = document.querySelector('#price-subtotal');
    if (priceEl) {
      priceEl.classList.remove('tw-loading-price');
    }
  }

  /**
   * Copy the add-to-cart subtotal price and coverage info from the response.
   * Also ensure the add-to-cart button is enabled.
   */
  function updateAddToCartArea(doc) {
    var newSubtotal = doc.querySelector('#price-subtotal');
    var currentSubtotal = document.querySelector('#price-subtotal');

    if (newSubtotal && currentSubtotal) {
      // Update both the text content and data-price attribute
      currentSubtotal.textContent = newSubtotal.textContent;
      var subtotalPrice = newSubtotal.getAttribute('data-price') || '';
      currentSubtotal.setAttribute('data-price', subtotalPrice);
      if (typeof window.jQuery !== 'undefined') {
        window.jQuery(currentSubtotal).data('price', subtotalPrice);
      }
    }

    var newTitleSub = doc.querySelector('.title-sub-total');
    var currentTitleSub = document.querySelector('.title-sub-total');
    if (newTitleSub && currentTitleSub) {
      currentTitleSub.innerHTML = newTitleSub.innerHTML;

      var responseUnitLabel = currentTitleSub.querySelector('.unit-label');
      var responseQtyNumber = currentTitleSub.querySelector('.price-unit-qty-number');
      var responseQtyTitle = currentTitleSub.querySelector('.price-unit-qty-title');
      var responseCovers = currentTitleSub.querySelector('.price-unit-covers');
      var responseConversPrice = currentTitleSub.querySelector('.convers-price');

      if (responseUnitLabel) {
        var unitLabelText = responseUnitLabel.textContent;
        var unitLabelData = responseUnitLabel.getAttribute('data-label');
        document.querySelectorAll('.unit-label').forEach(function(node) {
          node.textContent = unitLabelText;
          if (unitLabelData !== null) {
            node.setAttribute('data-label', unitLabelData);
            if (typeof window.jQuery !== 'undefined') {
              window.jQuery(node).data('label', unitLabelData);
            }
          }
        });
      }

      if (responseQtyNumber) {
        var qtyNumberText = responseQtyNumber.textContent;
        var qtyNumberData = responseQtyNumber.getAttribute('data-qty');
        document.querySelectorAll('.price-unit-qty-number').forEach(function(node) {
          node.textContent = qtyNumberText;
          if (qtyNumberData !== null) {
            node.setAttribute('data-qty', qtyNumberData);
            if (typeof window.jQuery !== 'undefined') {
              window.jQuery(node).data('qty', qtyNumberData);
            }
          }
        });
      }

      if (responseQtyTitle) {
        var qtyTitleText = responseQtyTitle.textContent;
        var qtyTitleData = responseQtyTitle.getAttribute('data-label');
        document.querySelectorAll('.price-unit-qty-title').forEach(function(node) {
          node.textContent = qtyTitleText;
          if (qtyTitleData !== null) {
            node.setAttribute('data-label', qtyTitleData);
            if (typeof window.jQuery !== 'undefined') {
              window.jQuery(node).data('label', qtyTitleData);
            }
          }
        });
      }

      if (responseCovers) {
        var coversText = responseCovers.textContent;
        var coversData = responseCovers.getAttribute('data-covers');
        document.querySelectorAll('.price-unit-covers').forEach(function(node) {
          node.textContent = coversText;
          if (coversData !== null) {
            node.setAttribute('data-covers', coversData);
            if (typeof window.jQuery !== 'undefined') {
              window.jQuery(node).data('covers', coversData);
            }
          }
        });
      }

      if (responseConversPrice) {
        var shouldShowConversPrice = window.getComputedStyle(responseConversPrice).display !== 'none';
        document.querySelectorAll('.convers-price').forEach(function(node) {
          node.style.display = shouldShowConversPrice ? '' : 'none';
        });
      }
    }

    // Ensure add-to-cart button is enabled
    var addBtn = document.querySelector('#AddToCart-' + sectionId);
    if (addBtn) {
      addBtn.disabled = false;
    }
    // var addBtnText = document.querySelector('#AddToCartText-' + sectionId);
    // if (addBtnText && typeof theme !== 'undefined' && theme.productStrings) {
    //   addBtnText.textContent = theme.productStrings.addToCart || 'Add to Cart';
    // }
    // Show "available" state
    var itemAvail = document.querySelectorAll('.item-available');
    itemAvail.forEach(function(el) { el.classList.remove('tw-hidden'); });
    var itemNotAvail = document.querySelectorAll('.item-not-available');
    itemNotAvail.forEach(function(el) { el.classList.add('tw-hidden'); });
  }

  /**
   * Copy SKU and stock label from the response.
   */
  function updateSKUAndStockLabel(doc) {
    var newSku = doc.querySelector('.variant-sku');
    var currentSku = document.querySelector('.variant-sku');
    if (newSku && currentSku) {
      currentSku.innerHTML = newSku.innerHTML;
    }

    var newStockLbl = doc.querySelector('.stockLbl');
    var currentStockLbl = document.querySelector('.stockLbl');
    if (newStockLbl && currentStockLbl) {
      currentStockLbl.className = newStockLbl.className;
      currentStockLbl.textContent = newStockLbl.textContent;
    }
  }

  function updateProductInfoAndSpecs(doc) {
    var newProductInfo = doc.querySelector('.tw-kanopi-product-info');
    var currentProductInfo = document.querySelector('.tw-kanopi-product-info');
    if (newProductInfo && currentProductInfo) {
      currentProductInfo.innerHTML = newProductInfo.innerHTML;
    }

    var newSpecsContent = doc.querySelector('#details-specs-content');
    var currentSpecsContent = document.querySelector('#details-specs-content');
    if (newSpecsContent && currentSpecsContent) {
      currentSpecsContent.innerHTML = newSpecsContent.innerHTML;
    }
  }

  function hydrateVariantQuantityMetaFromResponse(doc, variantId) {
    if (!variantId) return;

    if (typeof Shopify === 'undefined') {
      window.Shopify = {};
    }
    if (!Shopify.productMeta) {
      Shopify.productMeta = {};
    }
    if (!Shopify.productMeta[variantId]) {
      Shopify.productMeta[variantId] = {};
    }

    var responseSelect = doc.querySelector('#ProductSelect-' + sectionId);
    var responseOption = responseSelect
      ? Array.prototype.find.call(responseSelect.options, function(option) {
        return option.value === String(variantId) && (option.selected || option.hasAttribute('selected'));
      }) || Array.prototype.find.call(responseSelect.options, function(option) {
        return option.value === String(variantId);
      })
      : null;
    var responseOptionMin = responseOption ? responseOption.getAttribute('data-min-quantity') : null;
    var responseOptionMax = responseOption ? responseOption.getAttribute('data-max-quantity') : null;
    var responseOptionPrice = responseOption ? parseInt(responseOption.getAttribute('data-price-cents'), 10) : NaN;
    var responseOptionHasSurcharge = responseOption ? responseOption.getAttribute('data-has-surcharge') : null;
    var responseOptionProductSurcharge = responseOption ? responseOption.getAttribute('data-product-surcharge') : null;
    var responseOptionSurcharge = responseOption ? responseOption.getAttribute('data-surcharge') : null;

    if (responseOptionMin) {
      Shopify.productMeta[variantId].min_quantity = responseOptionMin;
    }
    if (responseOptionMax) {
      Shopify.productMeta[variantId].max_quantity = responseOptionMax;
    }
    if (!isNaN(responseOptionPrice) && responseOptionPrice > 0) {
      Shopify.productMeta[variantId].price = responseOptionPrice;
    }
    if (responseOptionHasSurcharge !== null) {
      Shopify.productMeta[variantId].has_surcharge = responseOptionHasSurcharge;
    }
    if (responseOptionProductSurcharge !== null) {
      Shopify.productMeta[variantId].product_surcharge = responseOptionProductSurcharge;
    }
    if (responseOptionSurcharge !== null) {
      Shopify.productMeta[variantId].surcharge = responseOptionSurcharge;
    }

    var scriptTexts = Array.prototype.map.call(doc.querySelectorAll('script'), function(script) {
      return script.textContent || '';
    });
    var assignmentPattern = new RegExp(
      'Shopify\\.productMeta\\["' + String(variantId).replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&') + '"\\]\\s*=\\s*\\{([\\s\\S]*?)\\n\\s*\\};',
      'm'
    );

    for (var i = 0; i < scriptTexts.length; i++) {
      var match = scriptTexts[i].match(assignmentPattern);
      if (!match) continue;

      var maxMatch = match[1].match(/max_quantity:\s*`([^`]*)`/);
      var minMatch = match[1].match(/min_quantity:\s*`([^`]*)`/);
      var priceUnitsQtyMatch = match[1].match(/priceUnitsTileQty:\s*"([^"]*)"/);
      var priceUnitsUnitMatch = match[1].match(/priceUnitsTileUnit:\s*"([^"]*)"/);
      var priceUnitsTilesMatch = match[1].match(/priceUnitsTileUnitTiles:\s*"([^"]*)"/);
      var sqftPerCartonMatch = match[1].match(/sqftPerCarton:\s*"([^"]*)"/);
      var pricePerSqftMatch = match[1].match(/pricePerSqft:\s*"([^"]*)"/);
      var pricePerSqftUnformattedMatch = match[1].match(/pricePerSqftUnformatted:\s*([0-9.]+)/);
      var hidePricePerUOMMatch = match[1].match(/hidePricePerUOM:\s*`([^`]*)`/);

      if (maxMatch && maxMatch[1] !== '') {
        Shopify.productMeta[variantId].max_quantity = maxMatch[1];
      }
      if (minMatch && minMatch[1] !== '') {
        Shopify.productMeta[variantId].min_quantity = minMatch[1];
      }
      if (priceUnitsQtyMatch) {
        Shopify.productMeta[variantId].priceUnitsTileQty = priceUnitsQtyMatch[1] || '';
      }
      if (priceUnitsUnitMatch) {
        Shopify.productMeta[variantId].priceUnitsTileUnit = priceUnitsUnitMatch[1] || '';
      }
      if (priceUnitsTilesMatch) {
        Shopify.productMeta[variantId].priceUnitsTileUnitTiles = priceUnitsTilesMatch[1] || '';
      }
      if (sqftPerCartonMatch) {
        Shopify.productMeta[variantId].sqftPerCarton = sqftPerCartonMatch[1] || '';
      }
      if (pricePerSqftMatch) {
        Shopify.productMeta[variantId].pricePerSqft = pricePerSqftMatch[1] || '';
      }
      if (pricePerSqftUnformattedMatch) {
        Shopify.productMeta[variantId].pricePerSqftUnformatted = parseFloat(pricePerSqftUnformattedMatch[1]);
      }
      if (hidePricePerUOMMatch) {
        Shopify.productMeta[variantId].hidePricePerUOM = hidePricePerUOMMatch[1] || '';
      }
      return;
    }
  }

  function getCurrentVariantSelectOption() {
    var currentSelect = document.querySelector('#ProductSelect-' + sectionId);
    if (!currentSelect || !currentSelect.value) return null;

    return Array.prototype.find.call(currentSelect.options, function(option) {
      return option.value === currentSelect.value;
    });
  }

  function getCurrentVariantMeta() {
    var currentSelect = document.querySelector('#ProductSelect-' + sectionId);
    var variantId = currentSelect && currentSelect.value ? currentSelect.value : null;

    if (!variantId || typeof Shopify === 'undefined' || !Shopify.productMeta) {
      return null;
    }

    if (!Shopify.productMeta[variantId]) {
      Shopify.productMeta[variantId] = {};
    }

    return Shopify.productMeta[variantId];
  }

  function syncCurrentVariantUnitPrice() {
    var meta = getCurrentVariantMeta();
    var selectedOption = getCurrentVariantSelectOption();
    var optionPrice = selectedOption ? parseInt(selectedOption.getAttribute('data-price-cents'), 10) : NaN;
    var variantPrice = !isNaN(optionPrice) && optionPrice > 0
      ? optionPrice
      : (meta && !isNaN(parseInt(meta.price, 10)) ? parseInt(meta.price, 10) : NaN);

    if (!meta || isNaN(variantPrice) || variantPrice <= 0) return;

    if (selectedOption) {
      meta.price = variantPrice;
      if (selectedOption.getAttribute('data-has-surcharge') !== null) {
        meta.has_surcharge = selectedOption.getAttribute('data-has-surcharge');
      }
      if (selectedOption.getAttribute('data-product-surcharge') !== null) {
        meta.product_surcharge = selectedOption.getAttribute('data-product-surcharge');
      }
      if (selectedOption.getAttribute('data-surcharge') !== null) {
        meta.surcharge = selectedOption.getAttribute('data-surcharge');
      }
    }

    var finalUnitPrice = variantPrice;
    if (typeof kanopiSurcharge !== 'undefined' && typeof kanopiSurcharge._getProductSurchargeAmount === 'function') {
      var surcharge = kanopiSurcharge._getProductSurchargeAmount(meta);
      var surchargeVal = variantPrice * (parseFloat(surcharge) / 100);
      var surchargePrice = 0;

      if (surcharge && !isNaN(surchargeVal)) {
        surchargePrice = String(surcharge).indexOf('-') !== -1 ? surchargeVal : Math.round(surchargeVal);
      }
      finalUnitPrice = variantPrice + surchargePrice;
    }

    document.querySelectorAll('.kanopi-current-variant-price:not(.grid-price)').forEach(function(priceEl) {
      priceEl.setAttribute('data-price', (finalUnitPrice / 100).toFixed(2));
      if (typeof window.jQuery !== 'undefined') {
        window.jQuery(priceEl).data('price', finalUnitPrice / 100);
      }
    });

  }

  function updateQuantityConstraints(doc) {
    var newQuantityInput = doc.querySelector('.product-single .product-form__input[name="quantity"]');
    var currentQuantityInput = document.querySelector('.product-single .product-form__input[name="quantity"]');

    if (newQuantityInput && currentQuantityInput) {
      var newMin = newQuantityInput.getAttribute('data-min');
      var newMax = newQuantityInput.getAttribute('data-max');
      var currentSelect = document.querySelector('#ProductSelect-' + sectionId);
      var selectedVariantId = currentSelect ? currentSelect.value : null;
      var selectedOption = currentSelect
        ? Array.prototype.find.call(currentSelect.options, function(option) {
          return option.value === selectedVariantId;
        })
        : null;
      var fallbackMeta = selectedVariantId && typeof Shopify !== 'undefined' && Shopify.productMeta
        ? Shopify.productMeta[selectedVariantId]
        : null;
      var selectedOptionMin = selectedOption ? selectedOption.getAttribute('data-min-quantity') : null;
      var selectedOptionMax = selectedOption ? selectedOption.getAttribute('data-max-quantity') : null;

      if (fallbackMeta && fallbackMeta.min_quantity) {
        currentQuantityInput.setAttribute('data-min', fallbackMeta.min_quantity);
      } else if (selectedOptionMin) {
        currentQuantityInput.setAttribute('data-min', selectedOptionMin);
      } else if (newMin !== null && newMin !== '') {
        currentQuantityInput.setAttribute('data-min', newMin);
      } else {
        currentQuantityInput.removeAttribute('data-min');
      }

      if (fallbackMeta && fallbackMeta.max_quantity) {
        currentQuantityInput.setAttribute('data-max', fallbackMeta.max_quantity);
      } else if (selectedOptionMax) {
        currentQuantityInput.setAttribute('data-max', selectedOptionMax);
      } else if (newMax !== null && newMax !== '') {
        currentQuantityInput.setAttribute('data-max', newMax);
      } else {
        currentQuantityInput.removeAttribute('data-max');
      }
    }

    var newValidationMessage = doc.querySelector('.quantity-validation-show-message');
    var currentValidationMessage = document.querySelector('.quantity-validation-show-message');
    if (newValidationMessage && currentValidationMessage) {
      currentValidationMessage.textContent = newValidationMessage.textContent;
      currentValidationMessage.className = newValidationMessage.className;
    }
  }

  /**
   * Update the gallery to show the correct image for the selected variant.
   * Finds the thumbnail and clicks it to trigger theme.js gallery update.
   * Safe because isSectionRendering is still true, so swatch change listener skips.
   */
  function updateGallery(doc) {
    var select = document.querySelector('#ProductSelect-' + sectionId);
    if (!select) return;

    var selectedOption = select.querySelector('option[value="' + select.value + '"]');
    if (!selectedOption) return;

    var imageId = selectedOption.getAttribute('data-image-id');
    if (!imageId) return;

    // Find the thumbnail with matching data-image-id
    var matchingThumb = document.querySelector('a[data-image-id="' + imageId + '"]');
    if (matchingThumb) {
      // isSectionRendering is still true, so the swatch change listener will skip
      $(matchingThumb).trigger('click');
    }
  }

  /**
   * Extract the selected variant from the Section Rendering API response
   * and store it so the patched _getVariantFromOptions can use it.
   */
  function extractSelectedVariant(doc) {
    // Try to get variant data from the new ProductJson
    var jsonEl = doc.querySelector('#ProductJson-' + sectionId);
    if (jsonEl) {
      try {
        var productData = JSON.parse(jsonEl.textContent);
        // Find the selected variant from the hidden select
        var newSelect = doc.querySelector('#ProductSelect-' + sectionId);
        var selectedOption = newSelect ? newSelect.querySelector('option[selected]') : null;
        var selectedId = selectedOption ? parseInt(selectedOption.value, 10) : null;

        if (selectedId && productData.variants) {
          for (var i = 0; i < productData.variants.length; i++) {
            if (productData.variants[i].id === selectedId) {
              window._highVariantSelectedVariant = productData.variants[i];
              // Also add to Shopify.productMeta so theme.js can access it
              if (typeof Shopify !== 'undefined' && !Shopify.productMeta) {
                Shopify.productMeta = {};
              }
              if (typeof Shopify !== 'undefined') {
                Shopify.productMeta[selectedId] = Object.assign(
                  {},
                  productData.variants[i],
                  Shopify.productMeta[selectedId] || {}
                );
              }
              return;
            }
          }
        }

        // If the variant wasn't in the ProductJson variants array (still beyond 250),
        // build a minimal variant object from available data
        if (selectedId) {
          var opt1 = doc.querySelector('.swatch[data-option-index="0"] .swatchInput:checked');
          var opt2 = doc.querySelector('.swatch[data-option-index="1"] .swatchInput:checked');
          var opt3 = doc.querySelector('.swatch[data-option-index="2"] .swatchInput:checked');
          var selOpt = selectedOption;

          window._highVariantSelectedVariant = {
            id: selectedId,
            title: selOpt ? selOpt.textContent.trim() : '',
            available: true,
            option1: opt1 ? opt1.value : null,
            option2: opt2 ? opt2.value : null,
            option3: opt3 ? opt3.value : null,
            price: productData.price,
            compare_at_price: productData.compare_at_price,
            inventory_management: 'shopify',
            featured_image: null,
            featured_media: null,
            sku: '',
            options: [
              opt1 ? opt1.value : null,
              opt2 ? opt2.value : null,
              opt3 ? opt3.value : null
            ].filter(Boolean),
            // Add default metafield properties for discount handling
            hasDiscount: false,
            discount: 0,
            hasVolDiscount: false,
            volumeQuantity: 0,
            volumeDiscount: 0,
            volumeMsg: '',
            volumeDiscountAmount: 0,
            applyVolumeDiscountAmount: false,
            has_surcharge: false,
            pricePerSqft: 0,
            hidePricePerUOM: false,
            mfgProcess: ''
          };

          // Try to get image from the selected option's data attributes
          if (selOpt && selOpt.dataset.imageId) {
            window._highVariantSelectedVariant.featured_image = {
              src: selOpt.dataset.imageId
            };
          }

          // Add to Shopify.productMeta so theme.js can access it
          if (typeof Shopify !== 'undefined') {
            if (!Shopify.productMeta) {
              Shopify.productMeta = {};
            }
            Shopify.productMeta[selectedId] = Object.assign(
              {},
              window._highVariantSelectedVariant,
              Shopify.productMeta[selectedId] || {}
            );
          }
        }
      } catch (e) {
        console.error('[HighVariant] Error extracting variant:', e);
      }
    }
  }

  /**
   * Update the hidden variant <select> with the new selected variant.
   */
  function updateVariantSelect(doc) {
    var newSelect = doc.querySelector('#ProductSelect-' + sectionId);
    var currentSelect = document.querySelector('#ProductSelect-' + sectionId);

    if (newSelect && currentSelect) {
      var resolvedValue = '';
      var resolvedSource = '';
      var selectedOption = newSelect.querySelector('option[selected]');

      if (pendingSelectionValues) {
        var matchedPendingOption = findSelectOptionByValues(newSelect, pendingSelectionValues);

        if (matchedPendingOption) {
          resolvedValue = matchedPendingOption.value;
          resolvedSource = 'pendingSelectionValues';
        }
      }

      if (!resolvedValue) {
        var checkedValues = getCheckedSwatchValuesByIndex();
        var matchedOption = findSelectOptionByValues(newSelect, checkedValues);

        if (matchedOption) {
          resolvedValue = matchedOption.value;
          resolvedSource = 'checkedSwatches';
        }
      }

      if (!resolvedValue && newSelect.value) {
        resolvedValue = newSelect.value;
        resolvedSource = 'newSelect.value';
      }

      if (!resolvedValue && selectedOption) {
        resolvedValue = selectedOption.value;
        resolvedSource = 'option[selected]';
      }

      if (!resolvedValue && window._highVariantSelectedVariant && window._highVariantSelectedVariant.id) {
        resolvedValue = String(window._highVariantSelectedVariant.id);
        resolvedSource = '_highVariantSelectedVariant';
      }

      currentSelect.innerHTML = newSelect.innerHTML;

      if (resolvedValue) {
        currentSelect.querySelectorAll('option[selected]').forEach(function(option) {
          option.removeAttribute('selected');
        });

        currentSelect.value = resolvedValue;

        var resolvedOption = currentSelect.querySelector('option[value="' + resolvedValue + '"]');
        if (resolvedOption) {
          resolvedOption.setAttribute('selected', 'selected');
          syncSelectedVariantFromOption(resolvedOption);
        }

        // Do NOT dispatch change — it triggers _onSelectChange → _getCurrentOptions
        // which crashes. We update price/images/SKU directly from the response instead.
      }
    }
  }

  function findSelectOptionByValues(select, optionValuesToMatch) {
    if (!select || !optionValuesToMatch || !optionValuesToMatch.length) return null;

    return Array.prototype.find.call(select.options, function(option) {
      var optionValues = option.textContent.split(' / ').map(function(value) {
        return value.trim();
      });

      return optionValuesToMatch.every(function(value, index) {
        return value && optionValues[index] === value;
      });
    });
  }

  function syncSelectedVariantFromOption(option) {
    if (!option || !option.value) return;

    var selectedId = parseInt(option.value, 10);
    var optionValues = option.textContent.split(' / ').map(function(value) {
      return value.trim();
    });
    var existingMeta = typeof Shopify !== 'undefined' && Shopify.productMeta && Shopify.productMeta[selectedId]
      ? Shopify.productMeta[selectedId]
      : {};
    var optionPrice = parseInt(option.getAttribute('data-price-cents'), 10);
    var selectedVariant = Object.assign({}, existingMeta, {
      id: selectedId,
      title: option.textContent.trim(),
      available: true,
      option1: optionValues[0] || null,
      option2: optionValues[1] || null,
      option3: optionValues[2] || null,
      options: optionValues
    });

    if (!isNaN(optionPrice) && optionPrice > 0) {
      selectedVariant.price = optionPrice;
    }

    var optionMaterialId = option.getAttribute('data-material-id');
    if (optionMaterialId) {
      selectedVariant.material_id = optionMaterialId;
    }

    if (option.dataset.imageId) {
      selectedVariant.featured_image = { src: option.dataset.imageId };
    }

    window._highVariantSelectedVariant = selectedVariant;

    if (typeof Shopify !== 'undefined') {
      if (!Shopify.productMeta) {
        Shopify.productMeta = {};
      }
      Shopify.productMeta[selectedId] = selectedVariant;
    }
  }

  /**
   * Update the ProductJson script tag with new product data.
   */
  function updateProductJson(doc) {
    var newJson = doc.querySelector('#ProductJson-' + sectionId);
    var currentJson = document.querySelector('#ProductJson-' + sectionId);

    if (newJson && currentJson) {
      currentJson.innerHTML = newJson.innerHTML;
    }
  }

  /**
   * Update inventory quantity spans.
   */
  function updateInventorySpans(doc) {
    var newSpans = doc.querySelectorAll('[id^="pvr-"]');
    newSpans.forEach(function(newSpan) {
      var currentSpan = document.getElementById(newSpan.id);
      if (currentSpan) {
        currentSpan.textContent = newSpan.textContent;
      }
    });
  }

  /**
   * Listen for swatch changes on high-variant products and trigger
   * Section Rendering API updates.
   */
  function initHighVariantListeners() {
    document.addEventListener('click', function(event) {
      if (!isFetchingSection || isSectionRendering) return;

      var input = getSwatchInputFromTarget(event.target);
      if (!input || !input.classList.contains('swatchInput') || input.checked) return;

      queuedSwatchInput = input;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }, true);

    // Listen for swatch radio button changes
    var inputs = document.querySelectorAll('.swatch .swatchInput');
    
    // Monitor hidden select changes to see if theme.js is updating it
    var select = document.querySelector('#ProductSelect-' + sectionId);
    if (select) {
      var lastSelectValue = select.value;
      var selectObserver = setInterval(function() {
        if (select.value !== lastSelectValue) {
          lastSelectValue = select.value;
        }
      }, 50);
    }
    
    inputs.forEach(function(input) {
      var swatchElement = input.closest('.swatch-element');
      var prefetchTargets = [input];
      if (swatchElement) {
        prefetchTargets.push(swatchElement);
      }

      prefetchTargets.forEach(function(target) {
        target.addEventListener('pointerenter', function() {
          schedulePrefetchForInput(input);
        });
        target.addEventListener('focus', function() {
          schedulePrefetchForInput(input);
        });
        target.addEventListener('touchstart', function() {
          schedulePrefetchForInput(input);
        }, { passive: true });
      });

      input.addEventListener('change', function() {
        // Skip if this change was triggered by our own Section Rendering update
        if (isSectionRendering) {
          return;
        }

        if (isFetchingSection) {
          queuedSwatchInput = input;
          return;
        }

        var swatchGroup = input.closest('.swatch[data-option-index]');
        var changedOptionIndex = swatchGroup ? parseInt(swatchGroup.dataset.optionIndex, 10) : null;

        if (
          activeChangeContext &&
          typeof changedOptionIndex === 'number' &&
          changedOptionIndex > activeChangeContext.changedOptionIndex
        ) {
          return;
        }

        setHighVariantLoading(true);
        activeChangeContext = activeChangeContext && activeChangeContext.changedOptionIndex < changedOptionIndex
          ? activeChangeContext
          : {
              changedOptionIndex: changedOptionIndex,
              changedValue: input.value
            };
        pendingSelectionValues = getCheckedSwatchValuesByIndex();
        if (typeof changedOptionIndex === 'number') {
          pendingSelectionValues[changedOptionIndex] = input.value;
        }

        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(function() {
          if (
            activeChangeContext &&
            typeof changedOptionIndex === 'number' &&
            changedOptionIndex > activeChangeContext.changedOptionIndex
          ) {
            setHighVariantLoading(false);
            flushQueuedSwatchClick();
            return;
          }

          var optionValueIds = getOptionValueIdsForSelectionValues(
            pendingSelectionValues,
            changedOptionIndex
          );
          if (!optionValueIds.length) {
            optionValueIds = getSelectedOptionValueIds();
          }
          if (optionValueIds.length > 0) {
            fetchAndUpdateSection(optionValueIds, changedOptionIndex, false);
          } else {
            setHighVariantLoading(false);
            flushQueuedSwatchClick();
          }
        }, 20);
      });
    });

    hydrateInitialHighVariantState();
    schedulePageLoadWarmup();
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHighVariantListeners);
  } else {
    initHighVariantListeners();
  }

  window.addEventListener('pageshow', function() {
    hydrateInitialHighVariantState();
  });
})();
